import { parse, serialize, systemFieldOf, isElementIdSegment, elementIdOf } from './kql';
import { normalizeId } from './ids';
import { formatCount } from './counts';
import { toBase64Url, fromBase64Url } from './base64url';

/**
 * COMBINED LISTS — the pure half (KPE-034, KPR-1022; keepr-api
 * docs/SCHEMA.md § Combined lists; keepr-web docs/plans/cross-collection-views.md).
 *
 * A combined list is one card read across every collection a person lists.
 * Its DEFINITION is `{ match, exclude?, include? }`: what keepr matches (a
 * bundle card, a card, hand-picked cards, an element set), what the person
 * left out, and the cards they added by hand. Nothing here talks to the API.
 */

/** The page's routes. An unsaved list carries its definition in `def`. */
export const COMBINED_LIST_NEW_PATH = '/lists/new';
export const combinedListPath = id => `/lists/${id}`;

/** The synthetic card the vocabulary is read as (buildFilterFields, the sort). */
export const COMBINED_CARD_ID = 'c0'.repeat(12);

// ── The definition in the address ──────────────────────────────────────────

const isPlainObject = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/**
 * A match the API would read: exactly one of `bundleCard`, `card`, `cards`,
 * `elementSet`. Anything else is not a match (the page says "not found",
 * never sends it).
 */
export function isMatch(match) {
	if (!isPlainObject(match)) return false;
	const keys = Object.keys(match);
	if (keys.length !== 1) return false;
	const [key] = keys;
	const value = match[key];
	if (key === 'bundleCard') return isPlainObject(value) && typeof value.package === 'string' && typeof value.localId === 'string';
	if (key === 'card') return typeof value === 'string' && Boolean(value);
	if (key === 'cards') return Array.isArray(value) && value.length > 0 && value.every(id => typeof id === 'string' && id);
	if (key === 'elementSet') return (typeof value === 'string' && Boolean(value)) || (isPlainObject(value) && typeof value.package === 'string' && typeof value.localId === 'string');
	return false;
}

/** The definition's own fields, cleaned: `{ match, exclude, include }`. */
export function cleanDefinition(def) {
	if (!isPlainObject(def) || !isMatch(def.match)) return null;
	const exclude = (Array.isArray(def.exclude) ? def.exclude : [])
		.filter(entry => isPlainObject(entry) && normalizeId(entry.collection))
		.map(entry => (entry.card
			? { collection: normalizeId(entry.collection), card: normalizeId(entry.card) }
			: { collection: normalizeId(entry.collection) }));
	const include = (Array.isArray(def.include) ? def.include : []).map(normalizeId).filter(Boolean);
	return { match: def.match, exclude, include: [...new Set(include)] };
}

export function encodeDefinition(def) {
	const clean = cleanDefinition(def);
	if (!clean) return '';
	const out = { match: clean.match };
	if (clean.exclude.length) out.exclude = clean.exclude;
	if (clean.include.length) out.include = clean.include;
	return toBase64Url(JSON.stringify(out));
}

/** The `def` of an unsaved list's address, or null when it is not one. */
export function decodeDefinition(text) {
	if (!text || typeof text !== 'string') return null;
	try {
		return cleanDefinition(JSON.parse(fromBase64Url(text)));
	} catch {
		return null;
	}
}

/**
 * The address of an unsaved list: its definition, and the List's own state
 * (`q`, `sort`, `dir`, `view`) plus `from` — the collection a scope switch
 * came from, so "This collection" can go back.
 */
export function newCombinedListHref(def, { q, sort, dir, view, from } = {}) {
	const params = new URLSearchParams();
	params.set('def', encodeDefinition(def));
	if (q) params.set('q', q);
	if (sort) params.set('sort', sort);
	if (dir) params.set('dir', dir);
	if (view) params.set('view', view);
	if (from) params.set('from', from);
	return `${COMBINED_LIST_NEW_PATH}?${params.toString()}`;
}

// ── Matches ────────────────────────────────────────────────────────────────

/** One string per match, the same whoever sends it: for comparing and for keys. */
export function matchKey(match) {
	if (!isMatch(match)) return '';
	if (match.bundleCard) return `bundle:${match.bundleCard.package}:${match.bundleCard.localId}`;
	if (match.card) return `card:${normalizeId(match.card)}`;
	if (match.cards) return `cards:${[...match.cards].map(normalizeId).sort().join(',')}`;
	const set = match.elementSet;
	return typeof set === 'string' ? `set:${normalizeId(set)}` : `set:${set.package}:${set.localId}`;
}

export const sameMatch = (a, b) => Boolean(matchKey(a)) && matchKey(a) === matchKey(b);

/**
 * The match a collection's card would be found by, as the API's suggestions
 * name it: a card installed from a bundle by its bundle card (the install
 * stamp `clonedFrom.packageId` + `localId`, served to every reader of the
 * card), any other card by its own id.
 */
export function matchForCard(card) {
	const id = normalizeId(card?._id);
	if (!id) return null;
	const stamp = card?.clonedFrom;
	const pkg = stamp && (stamp.packageId || stamp.package);
	if (pkg && stamp.localId) return { bundleCard: { package: String(pkg), localId: String(stamp.localId) } };
	return { card: id };
}

/**
 * Which of a person's matches a card is part of: their saved list on it (it
 * wins: the switch opens what they saved), else the suggestion. `null` when
 * neither holds it — no other collection the person lists holds this card.
 */
export function combinedTargetFor(card, { suggestions = [], lists = [] } = {}) {
	const match = matchForCard(card);
	if (!match) return null;
	const key = matchKey(match);
	const list = lists.find(entry => matchKey(entry?.match) === key);
	if (list) return { kind: 'saved', match, list };
	const suggestion = suggestions.find(entry => matchKey(entry?.match) === key);
	if (suggestion) return { kind: 'suggested', match, suggestion };
	return null;
}

// ── Names ──────────────────────────────────────────────────────────────────

/** A card's plural ("Recipes"), from its label or its name. */
export function cardPlural(card) {
	const label = card?.label;
	if (label && typeof label === 'object' && label.plural) return label.plural;
	const singular = (label && typeof label === 'object' && label.singular) || card?.name || 'Items';
	return /s$/i.test(singular) ? singular : `${singular}s`;
}

export function cardSingular(card) {
	const label = card?.label;
	return (label && typeof label === 'object' && label.singular) || card?.name || 'Item';
}

const labelOf = entry => {
	const label = entry?.label;
	if (label && typeof label === 'object') return label.singular || label.plural || entry?.name || '';
	return (typeof label === 'string' && label) || entry?.name || '';
};

/**
 * What a source's note says, quietly, after its badge: read only partly, or
 * missing what the filter and sort name. Never an error: the list still
 * answers (the never-breaks-a-list rule).
 */
export function sourceNotes(source) {
	const notes = [];
	if (source?.status === 'partial') notes.push('Partly read; some rows may be missing');
	(source?.missing || []).forEach((miss) => {
		const name = miss?.name || 'an element';
		notes.push(miss?.reason === 'type' ? `${name} is a different kind here` : `Doesn’t have ${name}`);
	});
	return notes;
}

export function sourceCountText(source) {
	if (source?.count === null || source?.count === undefined) return '';
	if (source.countCapped) return '1,000+';
	return Number(source.count).toLocaleString();
}

export const sourceKey = source => `${normalizeId(source?.collection?._id) || 'none'}:${normalizeId(source?.card?._id) || 'none'}`;

/** "4 sources" or "4 sources · 2 notes" — the phone's one button. */
export function sourcesSummary(sources = []) {
	const readable = sources.filter(source => source?.status !== 'unreadable');
	const notes = readable.reduce((sum, source) => sum + sourceNotes(source).length, 0);
	const head = formatCount(readable.length, 'source', 'sources') || 'No sources';
	return notes ? `${head} · ${formatCount(notes, 'note', 'notes')}` : head;
}

/**
 * The collection a source's count opens, with the same filter: its own List,
 * narrowed to its card, under the filter the query says it read
 * (`sources[].q`, in that source's own element ids).
 */
export function sourceListHref(source) {
	const collectionId = normalizeId(source?.collection?._id);
	if (!collectionId) return null;
	const params = new URLSearchParams();
	const cardId = normalizeId(source?.card?._id);
	if (cardId) params.set('card', cardId);
	if (source?.q) params.set('q', source.q);
	const query = params.toString();
	return `/collections/${collectionId}/items${query ? `?${query}` : ''}`;
}

/**
 * The card the sources' strip names a source by: only when two sources share
 * a collection, or the sources' cards are not all called the same (Zach,
 * 2026-10-20: a collection badge alone, the card when it is needed).
 */
export function sourcesNeedCard(sources = []) {
	const readable = sources.filter(source => source?.collection);
	const collections = new Set(readable.map(source => normalizeId(source.collection._id)));
	const names = new Set(readable.map(source => source?.card?.name || ''));
	return collections.size < readable.length || names.size > 1;
}

// ── The vocabulary as a card ───────────────────────────────────────────────

/**
 * The lined-up vocabulary as ONE card's elements, so the List's own filter
 * builder, chips and sort read it unchanged: each entry's id is its token,
 * which the builder writes as `#<token>` — what a combined list's filter
 * names (a saved one keeps the keys behind the tokens server-side).
 */
export function vocabularyCard(vocabulary, { name = 'Item' } = {}) {
	const elements = (vocabulary?.elements || [])
		.filter(entry => entry && entry.id && entry.name)
		.map(entry => ({
			id: entry.id,
			name: entry.name,
			label: entry.label && typeof entry.label === 'object' ? entry.label : { singular: labelOf(entry) },
			dataType: entry.dataType || 'text-small',
			options: entry.options || {},
		}));
	return { _id: COMBINED_CARD_ID, name, label: { singular: name }, elements };
}

/** The sort choices: the List's dates, the title, and every element but a file. */
export function combinedSortOptions(vocabulary) {
	const base = [
		{ value: 'primaryDate', label: 'Primary date', dataType: 'date-time' },
		{ value: 'title', label: 'Title', dataType: 'text-small' },
		{ value: 'createdAt', label: 'Created date', dataType: 'date-time' },
		{ value: 'updatedAt', label: 'Updated date', dataType: 'date-time' },
	];
	const elements = (vocabulary?.elements || [])
		.filter(entry => entry && entry.id && entry.dataType !== 'file')
		.map(entry => ({ value: `#${entry.id}`, label: labelOf(entry), dataType: entry.dataType || 'text-small', options: entry.options || {} }));
	return [...base, ...elements];
}

// ── A collection's filter, read across ────────────────────────────────────

// System conditions that name things of ONE collection: its cards, its
// collections, its tags, its element sets. They stay behind.
const LOCAL_SYSTEM_ROOTS = new Set(['card', 'collection', 'tags', 'set']);

const columnsOf = element => (Array.isArray(element?.options?.columns) ? element.options.columns : []);

function entryByName(entries, name) {
	if (!name) return null;
	const matches = entries.filter(entry => entry?.name === name);
	return matches.length === 1 ? matches[0] : null;
}

/**
 * A collection List's filter, written for a combined list (Zach, 2026-10-20:
 * the scope switch carries the search AND the filter). Each condition is read
 * in the collection's own words — `#<id>` to its element's name on the card in
 * view — and written as the combined list's token for that name. A condition
 * that cannot be lined up (an element the list does not have, a lookup's walk
 * into another card, a facet, a comparison with another element) is left
 * behind, and named in `dropped` so the page can say so; so is a condition on
 * the collection's own cards, sub-collections, tags or element sets.
 *
 * `elements` are the card's elements in the collection (getElementsForCard);
 * `vocabulary` the combined list's. Returns `{ q, dropped: [label] }`.
 */
export function carryFilterAcross(q, { elements = [], vocabulary } = {}) {
	const text = `${q || ''}`.trim();
	if (!text) return { q: '', dropped: [] };
	let ast;
	try {
		ast = parse(text);
	} catch {
		return { q: '', dropped: ['Your filter'] };
	}
	if (!ast) return { q: '', dropped: [] };
	const entries = (vocabulary?.elements || []).filter(entry => entry?.id && entry?.name);
	const byId = new Map(elements.filter(el => el?.id).map(el => [String(el.id), el]));
	const byName = new Map(elements.filter(el => el?.name).map(el => [el.name, el]));
	const dropped = [];
	// Inside a block only the group is named: the block goes whole.
	let inBlock = 0;
	const drop = (label) => { if (!inBlock && label && !dropped.includes(label)) dropped.push(label); return null; };

	// The element a segment names here: `#<id>` by id, a bare name by name.
	const localElement = (segment, scope) => {
		if (isElementIdSegment(segment)) return scope.byId.get(String(elementIdOf(segment))) || null;
		return scope.byName.get(segment) || null;
	};

	const rewriteCmp = (node, scope, targets) => {
		const [root, ...rest] = node.path || [];
		if (node.facet || `${root || ''}`.startsWith('@')) return drop('A filter on linked items');
		if (scope === top && systemFieldOf(node) !== null) {
			if (LOCAL_SYSTEM_ROOTS.has(root)) return drop(root === 'tags' ? 'Tags' : root === 'set' ? 'Element set' : root === 'card' ? 'Card' : 'Collection');
			return node;
		}
		const element = localElement(root, scope);
		const label = element ? labelOf(element) : 'An element';
		if (!element) return drop(label);
		const entry = entryByName(targets, element.name);
		if (!entry) return drop(label);
		// A later segment that is an element (a lookup's walk) cannot be lined up.
		if (rest.some(segment => isElementIdSegment(segment))) return drop(label);
		if (node.value?.type === 'ref') return drop(label);
		const path = [`#${entry.id}`, ...rest];
		if (node.value?.type === 'block') {
			const groupScope = {
				byId: new Map(columnsOf(element).filter(col => col?.id).map(col => [String(col.id), col])),
				byName: new Map(columnsOf(element).filter(col => col?.name).map(col => [col.name, col])),
			};
			inBlock += 1;
			const inner = rewrite(node.value.v, groupScope, columnsOf(entry), label);
			inBlock -= 1;
			if (!inner) return drop(label);
			return { ...node, path, value: { ...node.value, v: inner } };
		}
		return { ...node, path };
	};

	// Inside a block every condition must line up, or the block is dropped
	// whole (a part of "an entry where…" is a different question).
	const rewrite = (node, scope, targets, blockLabel = null) => {
		if (!node) return null;
		if (node.op === 'cmp') return rewriteCmp(node, scope, targets);
		if (node.op === 'not') {
			const inner = rewrite(node.arg, scope, targets, blockLabel);
			return inner ? { ...node, arg: inner } : null;
		}
		if (node.op === 'and' || node.op === 'or') {
			const args = (node.args || []).map(arg => rewrite(arg, scope, targets, blockLabel));
			if (blockLabel && args.some(arg => !arg)) return null;
			const kept = args.filter(Boolean);
			if (!kept.length) return null;
			return kept.length === 1 ? kept[0] : { ...node, args: kept };
		}
		return node;
	};

	const top = { byId, byName };
	const out = rewrite(ast, top, entries);
	return { q: out ? serialize(out) : '', dropped };
}

// ── The API's refusals, in words ───────────────────────────────────────────

const namesOf = list => (Array.isArray(list) ? list.map(entry => entry?.name).filter(Boolean) : []);

/**
 * What the API said no to, as the person reads it (keepr-api SCHEMA §
 * Combined lists: the definition's, a save's and a read's codes). Never a raw
 * code, never an id.
 */
export function refusalWords(error, fallback = 'Something went wrong. Try again.') {
	const data = error?.response?.data || {};
	const code = data.code || data.error;
	switch (code) {
		case 'unknown_element': {
			const names = namesOf(data.unknown);
			return names.length
				? `These collections don’t have ${names.join(', ')}. Take it out of the filter or the sort to save.`
				: 'The filter or the sort names something these collections don’t have.';
		}
		case 'ambiguous_element':
			return 'More than one element answers to a name in the filter. Pick it again from the filter.';
		case 'tags_not_saved':
			return 'A saved list can’t keep a tag in its filter. Take the tag condition off to save.';
		case 'too_many_sources':
			return `This list would read ${data.count || 'more than'} ${data.count ? 'sources' : `${data.limit || 25} sources`}; the most is ${data.limit || 25}. Leave some out first.`;
		case 'too_many_lists':
			return `You have ${data.limit || 50} saved lists, the most there can be. Delete one to save this.`;
		case 'invalid_name':
			return 'Enter a name of up to 100 characters.';
		case 'invalid_cursor':
			return 'The list changed. Start it again from the top.';
		default:
			return data.message || fallback;
	}
}
